import { useState } from 'react'
import { Box, Typography, Button, IconButton, CircularProgress, Avatar } from '@mui/material'
import { FormInput } from '../ui'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  StickyNote2 as NoteIcon
} from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'
import commentAssetIcon from '../../assests/commentIcon.svg'

export interface NoteUser {
  id: string
  name: string
  email: string
  avatarUrl?: string | null
}

export interface GenericNote {
  id: string
  content: string
  createdAt: string
  updatedAt: string
  createdBy?: NoteUser | null
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

function formatDate(iso: string) {
  const d = new Date(iso)
  const day = d.getDate().toString().padStart(2, '0')
  const month = d.toLocaleString('en-IN', { month: 'short' })
  const year = d.getFullYear().toString().slice(-2)
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()

  return `${day}-${month}-${year} • ${time}`
}

// ── Sub-components ───────────────────────────────────────────────────────────

/**
 * Individual comment card with timeline styling
 */
export function CommentItem({
  note,
  onUpdate,
  onDelete,
  isUpdating = false,
  isLast = false
}: {
  note: GenericNote
  onUpdate?: (content: string) => Promise<void>
  onDelete?: () => void
  isUpdating?: boolean
  isLast?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(note.content)

  const handleSave = async () => {
    if (!editContent.trim() || !onUpdate) return
    await onUpdate(editContent.trim())
    setIsEditing(false)
  }

  return (
    <Box sx={{ display: 'flex', gap: 2.5 }}>
      {/* Timeline Column */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: '8px',
          bgcolor: '#f8fafc', border: '1px solid #f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          <img src={commentAssetIcon} alt="Comment" style={{ width: 18, height: 18, opacity: 0.5 }} />
        </Box>
        {!isLast && (
          <Box sx={{ flex: 1, width: '1.5px', bgcolor: '#f1f5f9', my: 1 }} />
        )}
      </Box>

      {/* Card Content */}
      <Box sx={{
        flex: 1, mb: isLast ? 0 : 3,
        bgcolor: '#fff',
        border: '1px solid #EEEEEE',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        transition: 'all 0.2s',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: '#EEEEEE' }
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, bgcolor: '#fdfdfe', borderBottom: '1px solid #f1f5f9'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{
              width: 32, height: 32, fontSize: '11px', fontWeight: 800,
              bgcolor: '#e0e7ff', color: '#4338ca'
            }}>
              {note.createdBy ? getInitials(note.createdBy.name) : 'S'}
            </Avatar>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
              <Typography fontSize="14px" fontWeight={700} color="#1e293b">
                {note.createdBy?.name ?? 'System'}
              </Typography>
              <Typography fontSize="13px" color="#64748b" fontWeight={400}>
                added a comment
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontSize="12px" color="#94a3b8" fontWeight={500}>
              {formatDate(note.createdAt)}
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {onUpdate && (
                <IconButton size="small" onClick={() => setIsEditing(true)}
                  sx={{ color: '#94a3b8', '&:hover': { color: themeColors.primary, bgcolor: '#f1f5f9' } }}>
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton size="small" onClick={onDelete}
                  sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: '#f1f5f9' } }}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Content Body */}
        <Box sx={{ px: ['16px'], py: ['10px'] }}>
          {isEditing ? (
            <Box>
              <FormInput
                autoFocus
                multiline
                rows={3}
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '14px',
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    '&.Mui-focused fieldset': { borderColor: themeColors.primary }
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => setIsEditing(false)}
                  sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#64748b' }}>
                  Cancel
                </Button>
                <Button size="small" variant="contained"
                  disabled={!editContent.trim() || isUpdating}
                  onClick={handleSave}
                  sx={{
                    textTransform: 'none', fontSize: '13px', fontWeight: 700, borderRadius: '8px', px: 2.5
                  }}>
                  {isUpdating ? <CircularProgress size={14} color="inherit" /> : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography fontSize="14px" color="#334155" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontWeight: 450 }}>
              {note.content}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Common input box for adding a new note
 */
export function CommentInput({
  onAdd,
  isAdding = false,
  placeholder = "Add a comment or note…"
}: {
  onAdd: (content: string) => Promise<void>
  isAdding?: boolean
  placeholder?: string
}) {
  const [content, setContent] = useState('')

  const handleAdd = async () => {
    if (!content.trim()) return
    await onAdd(content.trim())
    setContent('')
  }

  return (
    <Box sx={{ mb: 4 }}>
      <FormInput
        placeholder={placeholder}
        value={content}
        multiline
        rows={3}
        onChange={e => setContent(e.target.value)}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            fontSize: '14px',
            bgcolor: '#fff',
            '&:hover fieldset': { borderColor: themeColors.primary }
          }
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button
          variant="contained"
          size="small"
          disabled={!content.trim() || isAdding}
          onClick={handleAdd}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            px: 3,
            py: 1,
            bgcolor: themeColors.primary,
            '&:hover': { bgcolor: themeColors.primaryDark }
          }}
        >
          {isAdding ? <CircularProgress size={16} color="inherit" /> : 'Add Note'}
        </Button>
      </Box>
    </Box>
  )
}


/**
 * Empty state display for comments
 */
export function CommentsEmptyState({ message = "No comments yet. Start the conversation!" }: { message?: string }) {
  return (
    <Box sx={{
      textAlign: 'center',
      py: 8,
      bgcolor: '#f8fafc',
      borderRadius: '16px',
      border: '1px dashed #e2e8f0'
    }}>
      <NoteIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1.5 }} />
      <Typography fontSize="14px" color="#64748b" fontWeight={500}>
        {message}
      </Typography>
    </Box>
  )
}
